import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ThemeService } from '../../service/theme.service';
import { InvoiceGenerationStateService } from '../../service/invoice-generation-state.service';
import { IotService } from '../../service/iot.service';
import { BinsService } from '../../service/bins.service';
import { ClientService } from '../../service/client.service';
import { TranslateService } from '@ngx-translate/core';
import { PdfService } from '../../service/pdf.service';
import { environment } from '../../environments/environment';
import { FeeInputService } from '../../service/fee-input.service';

@Component({
  selector: 'app-installation-invoice',
  templateUrl: './installation-invoice.component.html',
  styleUrls: ['./installation-invoice.component.css'],
  standalone: false,
})
export class InstallationInvoiceComponent implements OnInit, OnDestroy {
  // Client context for invoice generation.
  clientId = '';
  clientName = '';
  clientAddress = '';
  // Selected device count used for per-device pricing.
  deviceCount = 0;
  // Editable fee inputs (preloaded from template).
  deviceFee = '';
  additionalFee = '';
  // Loading state for async template/device data.
  loading = false;
  // Loading state for device details.
  loadingDetails = false;
  // Selected device rows used for modal and PDF.
  selectedDeviceDetails: Array<{
    thingName: string;
    address: string;
    usageKey: string;
    usageLabel: string;
  }> = [];
  private destroy$ = new Subject<void>();
  private lastStateKey = '';

  constructor(
    private http: HttpClient,
    public theme: ThemeService,
    private invoiceState: InvoiceGenerationStateService,
    private router: Router,
    private iotService: IotService,
    public binService: BinsService,
    private clientService: ClientService,
    private translate: TranslateService,
    private pdf: PdfService,
    public feeInput: FeeInputService
  ) {}

  ngOnInit(): void {
    // React to workflow state changes so filters stay dynamic.
    this.invoiceState.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => this.applyState(state.clientId, state.devices));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyState(clientId: string, devices: string[]): void {
    if (!clientId || devices.length === 0) {
      this.router.navigate(['/invoice-generate']);
      return;
    }

    const nextKey = `${clientId}::${devices.join('|')}`;
    if (nextKey === this.lastStateKey) {
      return;
    }
    this.lastStateKey = nextKey;

    this.clientId = clientId;
    this.deviceCount = devices.length;
    this.loadInvoiceTemplate(clientId);
    this.loadClientName(clientId);
    this.loadSelectedDeviceDetails();
  }

  get isBusy(): boolean {
    return this.loading || this.loadingDetails;
  }

  get canGenerate(): boolean {
    return !this.isBusy && this.selectedDeviceDetails.length > 0;
  }

  private async loadInvoiceTemplate(clientId: string): Promise<void> {
    // Fetch device/additional fees from template API.
    this.loading = true;
    try {
      const url =
        environment.api.stage +
        environment.api.route.getClientInvoiceTemplate +
        '&client_id=' +
        clientId;
      const response = await firstValueFrom(this.http.get<any>(url));
      const template = Array.isArray(response) ? response[0] : response;
      if (template) {
        this.deviceFee = this.formatFeeValue(template.device_fee);
        this.additionalFee = this.formatFeeValue(template.additional_fee);
      }
    } catch (error) {
      console.error('[InstallationInvoice] Failed to load template', error);
      this.deviceFee = '';
      this.additionalFee = '';
    } finally {
      this.loading = false;
    }
  }

  private formatFeeValue(value: number | string | null | undefined): string {
    // Normalize fee display to 2 decimals.
    if (value === null || value === undefined || value === '') return '';
    const numericValue = typeof value === 'string' ? value : String(value);
    return this.feeInput.formatFeeValue(numericValue);
  }

  private async loadSelectedDeviceDetails(): Promise<void> {
    // Build device rows for modal/PDF.
    if (!this.invoiceState.devices.length) {
      this.selectedDeviceDetails = [];
      return;
    }

    this.loadingDetails = true;
    try {
      try {
        if (!this.iotService.devicesArray || this.iotService.devicesArray.length === 0) {
          await this.iotService.adminDeviceArray();
        }
      } catch (error) {
        console.error('[InstallationInvoice] Failed to load devices', error);
      }

      const deviceSet = new Set(this.invoiceState.devices);
      const devices = (this.iotService.devicesArray || []).filter((row: any) =>
        deviceSet.has(row.thingName)
      );

      this.selectedDeviceDetails = devices.map((row: any) => {
        const usageKey = this.binService.getBinUsage(row.bin?.bin_usage ?? '');
        return {
          thingName: row.thingName,
          address: row.bin?.bin_address || this.translate.instant('withoutBin'),
          usageKey,
          usageLabel: usageKey ? this.translate.instant(usageKey) : '',
        };
      });
    } finally {
      this.loadingDetails = false;
    }
  }

  private async loadClientName(clientId: string): Promise<void> {
    // Resolve display name + address for tax calculation.
    try {
      await this.clientService.getClientsForSelect();
      const clients = this.clientService.clientsArray || [];
      const match = clients.find((c: any) => c.client_id === clientId);
      this.clientName = match?.legal_name || match?.client_name || clientId;
      this.clientAddress = match?.address || '';
    } catch (error) {
      console.error('[InstallationInvoice] Failed to load client name', error);
      this.clientName = clientId;
      this.clientAddress = '';
    }
  }

  async generateInvoicePdf(): Promise<void> {
    // Generate installation invoice PDF with taxes.
    const deviceFee = parseFloat(this.deviceFee || '0') || 0;
    const additionalFee = parseFloat(this.additionalFee || '0') || 0;

    await this.pdf.generateInstallationInvoicePdf({
      clientName: this.clientName || this.clientId,
      clientId: this.clientId,
      clientAddress: this.clientAddress,
      devices: this.selectedDeviceDetails.map((row) => ({
        thingName: row.thingName,
        address: row.address,
        usage: row.usageLabel || row.usageKey,
      })),
      deviceFee,
      additionalFee,
    });
  }

}
